import { useMatches } from '@tanstack/react-router'
import { useHead } from '@unhead/react'
import { compact } from 'lodash-es'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getActiveMeta, type RouteMeta } from '@/shared/route-meta/meta'
import type { TNullable } from '@/shared/types/common'
import { toAbsUrl } from '@/shared/utils/common.ts'

const APP_NAME = 'ONAY Bilim'
const DEFAULT_OG_IMAGE = '/favicon.png'

export function RouteMeta() {
  const { t, i18n } = useTranslation()
  const matches = useMatches()

  const meta = useMemo<TNullable<RouteMeta>>(() => getActiveMeta(matches), [matches])

  const pathname = matches.at(-1)?.fullPath || matches.at(-1)?.pathname || '/'
  const absoluteUrl =
    typeof window !== 'undefined' ? new URL(pathname, window.location.origin).href : undefined

  const title = useMemo(
    () => (meta?.title ? `${APP_NAME} — ${t(meta.title)}` : APP_NAME),
    [meta?.title, t]
  )

  const ogTitle = title
  const ogDescription = meta?.description
  const ogUrl = meta?.og?.url ?? meta?.canonical ?? absoluteUrl

  const rawOgImage = meta?.twitter?.image ?? meta?.og?.image ?? meta?.image ?? DEFAULT_OG_IMAGE
  const ogImage = toAbsUrl(rawOgImage)

  const ogType = meta?.og?.type ?? 'website'
  const ogSiteName = meta?.og?.siteName ?? APP_NAME
  const ogLocale = meta?.og?.locale ?? i18n.language?.replace('-', '_')

  const twitterCard = meta?.twitter?.card ?? (ogImage ? 'summary_large_image' : 'summary')
  const twitterImage = meta?.twitter?.image ?? ogImage

  useHead({
    title,
    link: compact([
      (meta?.canonical ?? absoluteUrl) && {
        rel: 'canonical',
        href: meta?.canonical ?? absoluteUrl!,
      },
    ]),
    meta: compact([
      meta?.description && { name: 'description', content: meta.description },
      meta?.noindex && { name: 'robots', content: 'noindex, nofollow' },

      // Open Graph
      { property: 'og:title', content: ogTitle },
      ogDescription && { property: 'og:description', content: ogDescription },
      ogUrl && { property: 'og:url', content: ogUrl },
      { property: 'og:type', content: ogType },
      { property: 'og:site_name', content: ogSiteName },
      ogLocale && { property: 'og:locale', content: ogLocale },
      ogImage && { property: 'og:image', content: ogImage },

      // Twitter
      { name: 'twitter:card', content: twitterCard },
      { name: 'twitter:title', content: ogTitle },
      ogDescription && { name: 'twitter:description', content: ogDescription },
      twitterImage && { name: 'twitter:image', content: twitterImage },
    ]),
  })

  return null
}
